/**
 * Watchlist catalog — types, collection pseudo-sources, and the region/id
 * translation logic shared by the picker UI (`./WatchlistPicker`), the fetch
 * hook (`./useWatchlists`), and the services that translate a picker
 * selection into a backend `data_source` token (screening_service,
 * monitoring_service, recent_searches_service, search_configurations_service
 * via empty-state.tsx).
 *
 * There is no bundled/offline catalog: the only source of watchlists is
 * `GET /sources/` (see `./useWatchlists`), and `id` is always the backend
 * `short_name` verbatim — the same value the `/search/` `data_source` param
 * expects, so a selection made in the picker flows straight through
 * `screening_service.buildSearchParams` as a valid backend token with no
 * translation needed.
 *
 * Region grouping is derived client-side from a source's `country` (the
 * backend has no region field of its own), matched against fixed code lists.
 */

import type { DataSource } from "@/src/services/data_sources_service";

export type DataSourceItem = {
  /** Unique id used for selection state. */
  id: string;
  /** Short code prefixed before the name, e.g. "INT", "UN", "EU". */
  code: string;
  name: string;
  region: DataSourceRegion;
};

export type DataSourceRegion = "International" | "Europe" | "Americas" | "Africa" | "Asia Oceania";

/** Collection-level pseudo-sources surfaced under the Collections tab.
 *  Their ids carry a `coll:` prefix so callers can distinguish them from
 *  individual data sources when rendering chips or applying filters. */
export type CollectionItem = {
  id: string;
  label: string;
};

export const COLLECTIONS: CollectionItem[] = [
  { id: "coll:sanctions-lists", label: "Sanctions Lists" },
  { id: "coll:pep-lists", label: "PEP Lists" },
  { id: "coll:crime-lists", label: "Crime Lists" },
  { id: "coll:high-risk-jurisdictions", label: "High Risk Jurisdictions" },
  { id: "coll:adverse-media", label: "Adverse Media" },
];

export function isCollectionId(id: string): boolean {
  return id.startsWith("coll:");
}

export function findCollection(id: string): CollectionItem | undefined {
  return COLLECTIONS.find((c) => c.id === id);
}

/** Collections hidden from the picker based on the company's plan entitlements. */
export function availableCollections(
  pepAllowed: boolean,
  adverseMediaEnabled: boolean,
): CollectionItem[] {
  return COLLECTIONS.filter((c) => {
    if (c.id === "coll:pep-lists") return pepAllowed;
    if (c.id === "coll:adverse-media") return adverseMediaEnabled;
    return true;
  });
}

// Verbatim from the client's production constants.ts (region arrays).
const EUROPE = new Set([
  "AD",
  "AL",
  "AT",
  "BA",
  "BE",
  "BG",
  "BY",
  "CH",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "EU",
  "FI",
  "FR",
  "GB",
  "GR",
  "HR",
  "HU",
  "IE",
  "IS",
  "IT",
  "LI",
  "LT",
  "LU",
  "LV",
  "MC",
  "MD",
  "ME",
  "MK",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "RS",
  "RU",
  "SE",
  "SI",
  "SK",
  "SM",
  "UA",
  "UK",
  "VA",
  "XK",
]);
const AMERICAS = new Set([
  "US",
  "CA",
  "MX",
  "AR",
  "BR",
  "CL",
  "CO",
  "PE",
  "VE",
  "CU",
  "EC",
  "BO",
  "PY",
  "UY",
  "GY",
  "SR",
  "BZ",
  "GT",
  "HN",
  "NI",
  "PA",
  "CR",
  "SV",
  // Caribbean
  "AG",
  "BB",
  "BS",
  "DM",
  "DO",
  "GD",
  "HT",
  "JM",
  "KN",
  "LC",
  "TT",
  "VC",
]);
const AFRICA = new Set([
  "NG",
  "ZA",
  "EG",
  "DZ",
  "KE",
  "GH",
  "UG",
  "TZ",
  "SN",
  "MA",
  "ET",
  "SD",
  "LY",
  "ZW",
  "CM",
  "CI",
  "MW",
  "MZ",
  "NA",
  "BW",
  "SL",
  "ML",
  "GN",
  "AO",
  "BF",
  "BI",
  "BJ",
  "CD",
  "CF",
  "CG",
  "CV",
  "DJ",
  "ER",
  "GA",
  "GM",
  "GQ",
  "GW",
  "KM",
  "LR",
  "LS",
  "MG",
  "MR",
  "MU",
  "NE",
  "RW",
  "SC",
  "SO",
  "SS",
  "ST",
  "SZ",
  "TD",
  "TG",
  "TN",
  "ZM",
]);
const ASIA_OCEANIA = new Set([
  "CN",
  "JP",
  "KR",
  "IN",
  "PK",
  "ID",
  "TH",
  "MY",
  "PH",
  "SG",
  "VN",
  "IL",
  "AU",
  "NZ",
  "BD",
  "LK",
  "NP",
  "MM",
  "KH",
  "LA",
  "AF",
  "SY",
  "IQ",
  "IR",
  "SA",
  "QA",
  "OM",
  "BH",
  "KW",
  "AE",
  // Middle East / Caucasus / Central Asia
  "AM",
  "AZ",
  "GE",
  "JO",
  "KG",
  "KZ",
  "LB",
  "PS",
  "TJ",
  "TM",
  "TR",
  "UZ",
  // South / Southeast Asia
  "BN",
  "BT",
  "MN",
  "MV",
  "TL",
  // Pacific
  "FJ",
  "FM",
  "KI",
  "MH",
  "NR",
  "PG",
  "PW",
  "SB",
  "TO",
  "TV",
  "VU",
  "WS",
  "YE",
]);

/**
 * Region tab for a source's `country` code.
 *
 * The production app has an "Others" bucket for unmatched countries; our popup
 * has no Others tab, so unmatched/empty/international codes fall through to
 * "International" as the catch-all. Re-evaluate with Ignacio once live `/sources/`
 * data shows how many sources land outside the four regional buckets.
 */
export function regionForCountry(country: string): DataSourceRegion {
  const code = (country || "").toUpperCase();
  if (EUROPE.has(code)) return "Europe";
  if (AMERICAS.has(code)) return "Americas";
  if (AFRICA.has(code)) return "Africa";
  if (ASIA_OCEANIA.has(code)) return "Asia Oceania";
  return "International";
}

/** Map a catalog `DataSource` to the `DataSourceItem` the picker consumes. */
export function dataSourceToItem(source: DataSource): DataSourceItem {
  const code = source.country || "INT";
  // Live `/sources/` names embed a "<country> / " prefix (e.g. "AR / MJUS …",
  // "INT / Asian Development Bank …"). The picker renders the code chip
  // separately, so strip an exact "<code> / " head to avoid "AR / AR / MJUS …".
  // Guarded by startsWith, so unprefixed (bundled-style) names pass through.
  const prefix = `${code} / `;
  const name = source.name.startsWith(prefix) ? source.name.slice(prefix.length) : source.name;
  return {
    id: source.shortName,
    code,
    name,
    region: regionForCountry(source.country),
  };
}

// ---------------------------------------------------------------------------
// Selection-id → backend `data_source` token translation.
//
// The data-source picker selects by an internal id: a `coll:`-prefixed
// collection id, or — for an individual source — the backend `short_name`
// itself (dataSourceToItem sets `id = source.shortName`). So the only real
// translation needed is collection id → backend collection keyword;
// anything else (an individual source id) already IS the token the `/search/`
// `data_source` param expects and passes through unchanged.
// ---------------------------------------------------------------------------

/** `coll:`-prefixed picker id → backend collection keyword. */
const COLLECTION_API_TOKENS: Record<string, string> = {
  "coll:sanctions-lists": "SANCTIONS-LISTS",
  "coll:pep-lists": "PEP-LISTS",
  "coll:crime-lists": "CRIME",
  "coll:high-risk-jurisdictions": "HIGH-RISK-JURISDICTIONS",
  "coll:adverse-media": "ADV-MEDIA",
};

/**
 * Translate a data-source picker selection id to the backend `data_source`
 * token. Collections → keyword; an individual source's id already is its
 * `short_name`, so it passes through unchanged.
 */
export function apiTokenForSelection(id: string): string {
  if (id.startsWith("coll:")) return COLLECTION_API_TOKENS[id] ?? id;
  return id;
}

const API_TOKEN_TO_COLLECTION: Record<string, string> = Object.fromEntries(
  Object.entries(COLLECTION_API_TOKENS).map(([k, v]) => [v, k]),
);

/**
 * Reverse of `apiTokenForSelection`: a backend `data_source` token → the
 * picker's selection id. A collection keyword resolves to its `coll:` id;
 * an individual source's `short_name` already is the picker id.
 */
export function selectionIdForApiToken(token: string): string {
  const t = token.trim();
  return API_TOKEN_TO_COLLECTION[t] ?? t;
}

const INDIVIDUAL_SOURCE_LABELS: Record<string, string> = {
  USERSOURCE: "User source",
};

export function dataSourceLabel(token: string): string {
  return (
    findCollection(selectionIdForApiToken(token))?.label ??
    INDIVIDUAL_SOURCE_LABELS[token.trim().toUpperCase()] ??
    token
  );
}

const COLLECTION_MEMBER_TOKENS: Record<string, string[]> = {
  "SANCTIONS-LISTS": [
    "ADB",
    "AR-MJUS",
    "AUL",
    "BEL",
    "BR-BCB",
    "BR-BCBAS",
    "BR-FGEIS",
    "BR-TCUDB",
    "BR-TCUIDPS",
    "CA-JFVCFO",
    "CA-NSCTL",
    "CA-UNSTR",
    "CANADA-SEMA",
    "CAP",
    "CCMC",
    "CFSP",
    "CMIC",
    "CNASNU",
    "DPL",
    "DTC",
    "EBRD-IE",
    "EL",
    "FINCEN",
    "FR-GELS",
    "FSE",
    "ID-DTTOT",
    "IL-NBCTF",
    "IN-TERRORIST",
    "IN-UNLAWFUL",
    "ISN",
    "JP-METIFUL",
    "LV-NSL",
    "MEU",
    "MY-MHA-MHAL",
    "NG-NIGSAC",
    "NL-NSLT",
    "NONSDN",
    "NS-MBS LIST",
    "NZ-FAT",
    "PH-ATC",
    "PK-NACTA",
    "PL-LPESS",
    "PLC",
    "SDN",
    "SG-MASEA",
    "SG-TSFA",
    "SSI",
    "SWISS-SECO",
    "UAE",
    "UK-PTGO",
    "UK-SANCTIONS",
    "UN",
    "US-CBP-WRO",
    "US-DHS-UFLPA",
    "US-DOS-CRS",
    "UVL",
    "WB-CDF",
    "WB-DFI",
    "ZA-FICTFS",
    "ZA-FSCAEA",
    "ZA-OCPO",
  ],
  "PEP-LISTS": ["PEP"],
  CRIME: ["EUROPOL", "FBI", "INTERPOL"],
  "HIGH-RISK-JURISDICTIONS": [
    "FATF",
    "HRJ-EU-HRTC",
    "HRJ-EU-NCJT",
    "HRJ-EU-RESTRICTIVE",
    "HRJ-FR-NCST",
    "HRJ-FR-SRG",
    "HRJ-MCAMSF-ETHR",
    "HRJ-MCAMSF-ETNC",
    "HRJ-UK-SRG",
    "HRJ-UN-SCSR",
    "OFAC-COMPREHENSIVE",
    "OFAC-MILITARY",
    "OFAC-OTHERS",
  ],
};

export function collapseKnownCollections(sources: string[]): string[] {
  const originalByKey = new Map<string, string>();
  for (const s of sources) {
    const key = s.trim().toUpperCase();
    if (!originalByKey.has(key)) originalByKey.set(key, s.trim());
  }
  const remaining = new Set(originalByKey.keys());
  const collapsed: string[] = [];
  for (const [token, members] of Object.entries(COLLECTION_MEMBER_TOKENS)) {
    if (members.every((m) => remaining.has(m))) {
      collapsed.push(token);
      members.forEach((m) => remaining.delete(m));
    }
  }
  return [...collapsed, ...[...remaining].map((key) => originalByKey.get(key)!)];
}

/** Short display code for a chip — the source's id, which is its `short_name`. */
export function shortNameFor(item: DataSourceItem): string {
  return item.id;
}
